import { Dispatch, SetStateAction } from 'react';
import { Flex, Divider, Box, Button, Text, Checkbox } from '@cad-arc';
import { VehicleOption } from '@mark43/cad-api';
import { CheckboxMultiselect } from '../common/components/checkbox-multiselect';
import { TowRotationProps } from './types';
import { useFilteredMultiVehiclesOptionsInDisplayedFormat } from './use-multi-vehicles-options-in-displayed-format';

export function MultiVehicleTowSection({
    towRotationProps,
    selectedMultiVehiclesIndexes,
    setSelectedMultiVehiclesIndexes,
    selectedMergedMultiVehiclesIndexes,
    setSelectedMergedMultiVehiclesIndexes,
    isLoading,
    multiVehiclesOptions,
    mergedMultiVehiclesOptions,
}: {
    towRotationProps?: TowRotationProps;
    selectedMultiVehiclesIndexes: number[];
    setSelectedMultiVehiclesIndexes: Dispatch<SetStateAction<number[]>>;
    isLoading: boolean;
    multiVehiclesOptions: VehicleOption[];
    mergedMultiVehiclesOptions: VehicleOption[];
    selectedMergedMultiVehiclesIndexes: number[];
    setSelectedMergedMultiVehiclesIndexes: Dispatch<SetStateAction<number[]>>;
}) {
    const { towVehicles } = towRotationProps?.eventForm.getValues() ?? {};
    const currentTowVehicle = towVehicles?.[towRotationProps?.towVehicleIndex ?? 0];
    const currentTowVehicleTitle =
        currentTowVehicle?.plateNumber ?? `Vehicle ${(towRotationProps?.towVehicleIndex ?? 0) + 1}`;
    const allVehiclesSelected =
        selectedMultiVehiclesIndexes.length === multiVehiclesOptions.length &&
        selectedMergedMultiVehiclesIndexes.length === mergedMultiVehiclesOptions.length;

    const vehiclesOptionsInDisplayedFormat = useFilteredMultiVehiclesOptionsInDisplayedFormat({
        multiVehiclesOptions,
        currentTowVehicleTitle,
        currentTowVehicleVinNumber: currentTowVehicle?.vinNumber,
    });
    const mergedVehiclesOptionsInDisplayedFormat = useFilteredMultiVehiclesOptionsInDisplayedFormat(
        {
            multiVehiclesOptions: mergedMultiVehiclesOptions,
            currentTowVehicleTitle,
            currentTowVehicleVinNumber: currentTowVehicle?.vinNumber,
        }
    );

    const handleMultiVehiclesChange = (value: (string | number)[]) => {
        setSelectedMultiVehiclesIndexes(value.sort() as number[]);
    };

    const handleMergedMultiVehiclesChange = (value: (string | number)[]) => {
        setSelectedMergedMultiVehiclesIndexes(value.sort() as number[]);
    };

    const handleSelectOrDeselectAll = () => {
        if (allVehiclesSelected) {
            setSelectedMultiVehiclesIndexes([]);
            setSelectedMergedMultiVehiclesIndexes([]);
        } else {
            setSelectedMultiVehiclesIndexes(
                vehiclesOptionsInDisplayedFormat.map((option) => option.value).sort()
            );
            setSelectedMergedMultiVehiclesIndexes(
                mergedVehiclesOptionsInDisplayedFormat.map((option) => option.value).sort()
            );
        }
    };

    return (
        <Flex flexDirection="column" alignItems="flex-start" width="100%">
            <Box w="full" mt={2} mb={3}>
                <Divider />
            </Box>
            <Box mb={1} pt={2}>
                <Text variant="headingXs">Multi-Vehicle Tow</Text>
            </Box>
            <Flex justifyContent="space-between" width="100%" alignItems="center">
                <Text fontSize="sm">Select vehicles for the same towing service</Text>
                <Button variant="ghost" onClick={handleSelectOrDeselectAll}>
                    {`${allVehiclesSelected ? 'Deselect' : 'Select'}`} All
                </Button>
            </Flex>
            <Checkbox isDisabled defaultChecked style={{ paddingLeft: 5 }}>
                {currentTowVehicleTitle}
            </Checkbox>
            <Flex>
                <CheckboxMultiselect
                    value={selectedMultiVehiclesIndexes}
                    options={vehiclesOptionsInDisplayedFormat}
                    onChange={handleMultiVehiclesChange}
                    columns={1}
                    disabledOptions={isLoading ? selectedMultiVehiclesIndexes : []}
                />
            </Flex>
            <Flex>
                <CheckboxMultiselect
                    value={selectedMergedMultiVehiclesIndexes}
                    options={mergedVehiclesOptionsInDisplayedFormat}
                    onChange={handleMergedMultiVehiclesChange}
                    columns={1}
                    disabledOptions={isLoading ? selectedMergedMultiVehiclesIndexes : []}
                    globalComplementaryLabel="(Merged)"
                />
            </Flex>
        </Flex>
    );
}
